import { decodeGetter } from '@/app/common/components/ag-grid/configs';
import URLRendererWithLink from '@/app/common/components/CellRenderers/URLRendererWithLink';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import healthScoreColorAndLabel from '@/helpers/healthScoreColorAndLabel';
import formatUtcDate from '@/helpers/formatUtcDate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown, ProgressBar, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useContext, useState, type ComponentType } from 'react';
import { useDispatch } from 'react-redux';
import { AiOutlineZoomIn } from 'react-icons/ai';
import analysisApi, {
  useCreateAnalysisMutation
} from '@/app/PageSpeed/api/analysisApi';
import { defaultMessage, errorMessage } from '@/utils/toast';
import { RemoveAnalysisContext } from '@/context/Context';
import { FaDesktop, FaMobileScreenButton } from 'react-icons/fa6';
import AnalysisStatusRenderer from '@/app/common/components/CellRenderers/AnalysisStatusRenderer';
import classNames from 'classnames';
import { showDrawer } from '@/app/common/reducers/drawer';
import type { AnalysisHistoryItem } from '@/app/PageSpeed/api/types/analysisApiTypes';
import { getAnalysisTaskIdFromCreateResponse } from '@/app/PageSpeed/api/types/analysisApiTypes';
import type { ColumnMenuTab, ICellRendererParams } from 'ag-grid-community';

const publicReportUrl = (slug?: string | null) =>
  slug
    ? `${import.meta.env.VITE_REACT_APP_MIZFA_TOOLS_PANEL_URL}/reporting/page_speed/${slug}`
    : '';

const ReportVisibilityIcon = ({
  isPublic,
  slug
}: {
  isPublic?: boolean;
  slug?: string | null;
}) => {
  const url = isPublic ? publicReportUrl(slug) : '';
  const icon = (
    <FontAwesomeIcon
      icon={isPublic ? 'lock-open' : 'lock'}
      title={isPublic ? 'گزارش عمومی' : 'گزارش خصوصی'}
    />
  );

  if (!url) {
    return (
      <ChildrensTooltip
        placement="top"
        delayToRemove={0}
        html={isPublic ? 'گزارش عمومی' : 'گزارش خصوصی'}
      >
        {icon}
      </ChildrensTooltip>
    );
  }

  return (
    <ChildrensTooltip
      placement="top"
      delayToRemove={0}
      html="مشاهده گزارش عمومی"
    >
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        onClick={event => event.stopPropagation()}
        className="text-decoration-none text-body"
      >
        {icon}
      </a>
    </ChildrensTooltip>
  );
};

const TypedURLRendererWithLink = URLRendererWithLink as ComponentType<any>;
const TypedAnalysisStatusRenderer = AnalysisStatusRenderer as ComponentType<any>;

const DeviceTypeIcons = ({ deviceType }: { deviceType?: string }) => {
  const isBoth = deviceType === 'both';
  const isDesktop = deviceType === 'desktop';
  const label = isBoth ? 'گوشی و رایانه' : isDesktop ? 'رایانه' : 'گوشی';
  return (
    <ChildrensTooltip placement="top" delayToRemove={0} html={label}>
      <span className="d-inline-flex align-items-center gap-1">
        {(isBoth || !isDesktop) && <FaMobileScreenButton title={label} />}
        {(isBoth || isDesktop) && <FaDesktop title={label} />}
      </span>
    </ChildrensTooltip>
  );
};

const scoreRingColor = (percent: number) => {
  if (percent >= 90) return 'var(--falcon-success)';
  if (percent >= 50) return 'var(--falcon-warning)';
  return 'var(--falcon-danger)';
};

const ScoreRing = ({
  percent,
  size = '1.75rem'
}: {
  percent: number;
  size?: string;
}) => {
  const clamped = Math.max(
    0,
    Math.min(100, Math.round(Number.isFinite(percent) ? percent : 0))
  );
  return (
    <span
      className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: `conic-gradient(${scoreRingColor(clamped)} ${clamped}%, var(--falcon-gray-300) 0)`,
        fontSize: 9,
        fontWeight: 600,
        lineHeight: 1
      }}
    >
      <span
        className="d-inline-flex align-items-center justify-content-center"
        style={{
          width: '77%',
          height: '77%',
          borderRadius: '50%',
          background: 'var(--ag-background-color, var(--falcon-card-bg))',
          color: 'var(--falcon-body-color)'
        }}
      >
        {clamped}
      </span>
    </span>
  );
};

const metricTooltipProps = {
  placement: 'top' as const,
  delayToRemove: 0
};

const MetricsCellRenderer = (params: CellParams) => {
  if (typeof params.data?.performance !== 'number') return '-';
  return (
    <div className="d-flex gap-1 h-100 justify-content-center align-items-center">
      <ChildrensTooltip {...metricTooltipProps} html={<>عملکرد (Performance)</>}>
        <ScoreRing percent={Number(params.value)} />
      </ChildrensTooltip>
      <ChildrensTooltip
        {...metricTooltipProps}
        html={<>دسترسی‌پذیری (Accessibility)</>}
      >
        <ScoreRing percent={Number(params.data.accessibility ?? 0)} />
      </ChildrensTooltip>
      <ChildrensTooltip
        {...metricTooltipProps}
        html={<>بهترین الگو (Best Practice)</>}
      >
        <ScoreRing percent={Number(params.data.best_practice ?? 0)} />
      </ChildrensTooltip>
      <ChildrensTooltip {...metricTooltipProps} html={<>سئو (SEO)</>}>
        <ScoreRing percent={Number(params.data.seo ?? 0)} />
      </ChildrensTooltip>
    </div>
  );
};

const menuTabs: ColumnMenuTab[] = [
  'columnsMenuTab',
  'filterMenuTab',
  'generalMenuTab'
];

type CellParams = ICellRendererParams<AnalysisHistoryItem> & {
  value?: any;
  data: AnalysisHistoryItem;
};

type AnalysisColumnConfigOptions = {
  onAnalysisStarted?: (jobId: string, url?: string) => void;
  hideActions?: boolean;
  selectedTaskIds?: string[];
  onSelectionChange?: (
    taskId: string,
    selected: boolean,
    row: AnalysisHistoryItem
  ) => void;
};

export const analysisColumnConfig = (
  domain_slug?: string,
  domain?: string,
  options?: AnalysisColumnConfigOptions
): any[] => [
  ...(options?.onSelectionChange
    ? [
        {
    field: '__analysis_selection__',
    headerName: '',
    type: 'array',
    searchable: false,
    sortable: false,
    filter: false,
    floatingFilter: false,
    noFilter: true,
    width: 45,
    pinned: 'right',
    cellRenderer: (params: CellParams) => {
      const taskId = String(params.data.task_id || params.data._id || '');
      const isChecked = Boolean(options?.selectedTaskIds?.includes(taskId));
      const isCompleted = Number(params.data.status) === 6;

      return (
        <input
          type="checkbox"
          checked={isChecked}
          disabled={
            !taskId ||
            !isCompleted ||
            (!isChecked && (options?.selectedTaskIds?.length || 0) >= 3)
          }
          aria-label={`انتخاب ${params.data.url || 'آدرس'}`}
          onChange={event =>
            options?.onSelectionChange?.(taskId, event.target.checked, params.data)
          }
          onClick={event => event.stopPropagation()}
        />
      );
    }
        }
      ]
    : []),
  {
    searchable: true,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    filterValueGetter: decodeGetter,
    field: 'url',
    type: 'string',
    title: 'نشانی',
    cellRenderer: (params: CellParams) => {
      return (
        <TypedURLRendererWithLink
          disabled={params.data.status !== 6}
          to={`/analysis/overview/${params.data._id}/${domain_slug}/${domain}`}
          url={params.value}
        />
      );
    },
    flex: 1,
    minWidth: 220
  },
  {
    searchable: false,
    menuTabs,
    sortable: false,
    filter: 'agTextColumnFilter',
    floatingFilter: false,
    filterValueGetter: decodeGetter,
    field: 'location_code',
    type: 'string',
    title: 'تنظیمات',
    width: 200,
    cellRenderer: (params: CellParams) => {
      const [opened, setOpened] = useState(false);
      const dispatch = useDispatch();
      return (
        <div className="d-flex gap-2 align-items-center">
          <ReportVisibilityIcon
            isPublic={Boolean(params.data.is_public)}
            slug={params.data.slug}
          />
          <ChildrensTooltip placement="top" delayToRemove={0} html={'کروم'}>
            <FontAwesomeIcon icon={['fab', 'chrome']} title="chrome" />
          </ChildrensTooltip>
          <DeviceTypeIcons deviceType={params.data.device_type} />

          <ChildrensTooltip
            placement="top"
            delayToRemove={0}
            html={
              params.value === 'not_ir'
                ? 'خارج'
                : params.value === 'google'
                  ? 'گوگل'
                  : params.value
            }
          >
            {params.value === 'not_ir' ? (
              <FontAwesomeIcon icon="globe" />
            ) : params.value === 'google' ? (
              <FontAwesomeIcon icon={['fab', 'google']} />
            ) : (
              <img
                src={`https://static.mizfa.tools/flags/${params.value}.webp`}
              />
            )}
          </ChildrensTooltip>
          <ChildrensTooltip
            placement="top"
            delayToRemove={0}
            html={params.data.internet_type?.toUpperCase() || 'نامشخص'}
          >
            <FontAwesomeIcon icon="wifi" />
          </ChildrensTooltip>
          {params.data.is_scheduled === true && (
            <ChildrensTooltip
              placement="top"
              delayToRemove={0}
              html="زمان‌بندی‌شده"
            >
              <FontAwesomeIcon icon="clock" title="زمان‌بندی‌شده" fixedWidth />
            </ChildrensTooltip>
          )}
          <span
            onClick={() => {
              setOpened(true);
              setTimeout(() => {
                setOpened(false);
              }, 1500);
            }}
          >
            {!opened ? (
              <AiOutlineZoomIn
                onClick={() => {
                  dispatch(
                    showDrawer({
                      key: 'analysis_settings',
                      payload: {
                        url: params.data.url,
                        location_code: params.data.location_code,
                        device_type: params.data.device_type,
                        internet_type: params.data.internet_type,
                        user_agent: params.data.user_agent,
                        headers: params.data.headers,
                        cookies: params.data.cookies,
                        is_public: params.data.is_public,
                        slug: params.data.slug
                      }
                    })
                  );
                }}
                className="cursor-pointer fs-0"
              />
            ) : (
              <Spinner
                animation="border"
                style={{
                  width: '16px',
                  marginBottom: '-3px',
                  paddingLeft: '3px',
                  height: '16px'
                }}
              />
            )}
          </span>
        </div>
      );
    }
  },
  {
    searchable: true,
    field: 'site_health',
    type: 'number',
    title: 'سلامت سایت',
    // tooltip: <p>{crawlTooltip.columns.health_score}</p>,
    width: 165,
    cellRenderer: (params: CellParams) =>
      params.data.status === 6 &&
      typeof params.value === 'number' ? (
        <div className="d-flex gap-1 align-items-center">
          <span>{params.value}%</span>
          <span className="w-100">
            <ProgressBar
              style={{ height: 5 }}
              className="rounded-pill align-middle"
              variant={healthScoreColorAndLabel(params.value).prClass}
              now={params.value}
            />
            <style>
              {`.bg-health-score-success {
              background-color:${healthScoreColorAndLabel(95).color} !important;
            }`}
              {`.bg-health-score-yellow {
              background-color:${healthScoreColorAndLabel(85).color} !important;
            }`}
              {`.bg-health-score-warning {
              background-color:${healthScoreColorAndLabel(75).color} !important;
            }`}
              {`.bg-health-score-danger {
              background-color:${healthScoreColorAndLabel(55).color} !important;
            }`}
            </style>
          </span>
        </div>
      ) : (
        '-'
      )
  },

  {
    searchable: true,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    field: 'full_loading_time',
    type: 'number',
    title: 'بارگذاری (ثانیه)',
    width: 140
  },
  {
    field: 'performance',
    type: 'array',
    title: 'معیار ها',
    width: 150,
    cellStyle: {
      textAlign: 'center',
      margin: '0px'
    },
    sortable: false,
    cellClass: 'over-flow',
    noFilter: true,
    searchable: false,
    cellRenderer: MetricsCellRenderer
  },
  {
    field: 'status',
    type: 'number',
    title: 'وضعیت',
    width: 118,
    searchable: false,
    // filter: 'agTextColumnFilter',
    // floatingFilter: true,
    // menuTabs,
    cellRenderer: (params: CellParams) => (
      <TypedAnalysisStatusRenderer status={params.value} />
    )
  },
  {
    field: 'created_at',
    type: 'string',
    title: 'تاریخ',
    width: 105,
    searchable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    menuTabs,
    valueFormatter: (params: { value?: string }) =>
      formatUtcDate(params.value),
    filterValueGetter: (params: { data?: AnalysisHistoryItem }) =>
      formatUtcDate(params.data?.created_at as string | undefined)
  },

  {
    field: 'url',
    type: 'array',
    title: ' ',
    hide: options?.hideActions,
    searchable: false,
    cellStyle: { textAlign: 'center', margin: '0px' },
    cellClass: 'over-flow',
    width: 70,
    pinned: 'left',
    noFilter: true,
    cellRenderer: (params: CellParams) => {
      const [createAnalysis] = useCreateAnalysisMutation();
      const dispatch = useDispatch();
      const { setAnalysisId, setAnalysisUrl, setShow, setStatus } = useContext(
        RemoveAnalysisContext
      );
      return (
        <span className="allaho_akbar_zIndex">
          <Dropdown
            onClick={e => e.stopPropagation()}
            align="end"
            className="allaho_akbar_zIndex overflow-visible btn-reveal-trigger d-inline-block me-0"
          >
            <Dropdown.Toggle
              className="text-600 btn-reveal"
              split
              variant="reveal"
              size="sm"
            >
              <FontAwesomeIcon icon="ellipsis-h" className="fs--2" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="border py-0">
              <Dropdown.Item
                as={Link}
                disabled={params.data.status !== 6}
                to={`/analysis/overview/${params.data._id}/${domain_slug}/${domain}`}
                className={classNames(
                  'link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1',
                  {
                    'disabled-link': params.data.status !== 6
                  }
                )}
              >
                <FontAwesomeIcon icon="eye" className="me-1 ms-2" />
                مشاهده
              </Dropdown.Item>
              <Dropdown.Item
                onClick={async () => {
                  // defaultMessage(
                  //   'واکاوی درحال انجام است. لطفا منتظر بمانید پس از اتمام به صفحه جزئیات هدایت میشوید.'
                  // );
                  defaultMessage('واکاوی درحال انجام است. لطفا منتظر بمانید.');
                  createAnalysis({
                    body: {
                      url: params.data.url ? [params.data.url] : [],
                      location_code: params.data.location_code || '',
                      device_type: params.data.device_type || '',
                      internet_type: params.data.internet_type,
                      user_agent: params.data.user_agent,
                      headers: params.data.headers,
                      cookies: params.data.cookies
                    },
                    country: params.data.location_code
                  })
                    .unwrap()
                    .then(res => {
                      const taskId = getAnalysisTaskIdFromCreateResponse(res);
                      if (taskId) {
                        options?.onAnalysisStarted?.(
                          taskId,
                          params.data.url
                        );
                      }
                      dispatch(
                        analysisApi.util.invalidateTags(['AnalysisHistory'])
                      );
                    })
                    .catch(err => {
                      console.error(err);
                      errorMessage('خطا در واکاوی');
                    });
                }}
                className="link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1"
              >
                <FontAwesomeIcon icon="undo-alt" className="me-1 ms-2" />
                جستجوی مجدد
              </Dropdown.Item>
              <Dropdown.Divider className="my-0" />
              {params.data.status === 6 ||
              params.data.status?.toString()?.startsWith('3') ||
              params.data.status === 49 ||
              params.data.status === 4 ? (
                <Dropdown.Item
                  onClick={() => {
                    setAnalysisId(params.data._id);
                    setAnalysisUrl(params.data.url || '');
                    setShow(true);
                    setStatus(6);
                  }}
                  className="link-600 text-danger fs--1 d-flex align-items-center gap-1 p-0 px-1"
                >
                  <FontAwesomeIcon
                    icon="trash-alt"
                    className="me-1 fs-0 ms-2"
                  />
                  حذف
                </Dropdown.Item>
              ) : (
                params.data.status === 2 && (
                  <Dropdown.Item
                    onClick={() => {
                      setAnalysisId(params.data._id);
                      setAnalysisUrl(params.data.url || '');
                      setShow(true);
                      setStatus(2);
                    }}
                    className="link-600 text-danger fs--1 d-flex align-items-center gap-1 p-0 px-1"
                  >
                    <FontAwesomeIcon
                      icon="trash-alt"
                      className="me-1 fs-0 ms-2"
                    />
                    لغو
                  </Dropdown.Item>
                )
              )}
            </Dropdown.Menu>
          </Dropdown>
        </span>
      );
    }
  }
];
